import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { throttle } from 'lodash-es';
import { TrackerCustomEvent, useTracker } from 'common-package';
import { DotLoading, List } from 'antd-mobile';
import { useLocation } from 'react-router';
import service from '@/service';
import { MultipleSelect, ScrollView } from '@/components';
import { ScrollViewRefAttrs } from '@/components/scroll-view';
import EmptyBox from '../EmptyBox';
import MemberRecordItem from './MemberRecordItem';
import { ARRIVAL_STATUS_OPTIONS, CREATE_TIME_OPTIONS, ORDER_TIMES_OPTIONS, SERVICE_SELECT_OPTIONS } from './enumdata';

import cx from './index.module.scss';

interface MemberRecordProps {}

const INIT_LOAD_SIZE = 12;

const formatSelectedOption = (data: string[], compareArr: { value: string; text: string }[]) => {
  return data?.length === compareArr?.length ? [] : data;
};

const MemberRecord: React.FC<MemberRecordProps> = props => {
  const { sendEvent } = useTracker();
  const { state } = useLocation();
  const scrollViewRef = useRef<ScrollViewRefAttrs>(null);
  const [filterDimension, setFilterDimension] = useState<FilterDimension>('totalOrders');
  const [filterRule, setFilterRule] = useState<SortOrder | FilterByRecentVisit[]>('desc');
  const [filterOptions, setFilterOptions] =
    useState<{ value: SortOrder | FilterByRecentVisit; text: string }[]>(ORDER_TIMES_OPTIONS);
  const [memberRecordList, setMemberRecordList] = useState<GetCustomerServiceResponse[]>([]);

  const [hasMoreData, setHasMoreData] = useState<boolean>(true);
  const [isLoading, setLoading] = useState<boolean>(true);

  const getSearchMemberRecord = useCallback(
    async (params: GetMemberRecordParams, reset: boolean = false) => {
      try {
        reset && setLoading(true);
        const { data } = await service.serviceRecordApi.getMemberRecord(params);
        reset ? setMemberRecordList(data) : setMemberRecordList([...memberRecordList, ...data]);
        const _hasMoreData = data?.length >= params?.pageSize;
        setHasMoreData(_hasMoreData);

        if (_hasMoreData) {
          scrollViewRef.current?.bsInstance?.finishPullUp();
        }
      } finally {
        reset && setLoading(false);
      }
    },
    [memberRecordList]
  );

  const getParams = (
    filterDimension: FilterDimension,
    filterRule: SortOrder | FilterByRecentVisit[]
  ): GetMemberRecordParams => {
    let params: GetMemberRecordParams = {
      sortBy: filterDimension,
      pageSize: INIT_LOAD_SIZE,
      cursor: 0,
    };
    if (filterDimension === 'visitTime') {
      const allSelectFilterByRecentVisit = formatSelectedOption(
        filterRule as FilterByRecentVisit[],
        ARRIVAL_STATUS_OPTIONS
      );
      params.filterByRecentVisit =
        allSelectFilterByRecentVisit?.length > 0 ? allSelectFilterByRecentVisit?.join(',') : '';
      params.sortOrder = 'desc';
    } else {
      params.sortOrder = filterRule as SortOrder;
      params.filterByRecentVisit = '';
    }
    return params;
  };

  const loadMore = useMemo(
    () =>
      throttle(async () => {
        const lastCursor = memberRecordList?.length;
        getSearchMemberRecord({ ...getParams(filterDimension, filterRule), pageSize: 6, cursor: lastCursor }, false);
      }, 600),
    [filterDimension, filterRule, getSearchMemberRecord, memberRecordList]
  );

  const handleSendEvent = useCallback(
    (filterDimension: FilterDimension, filterRule: SortOrder | FilterByRecentVisit[]) => {
      const filterLabel = SERVICE_SELECT_OPTIONS?.find(item => item.value === filterDimension)?.text;
      let filterVal = null;
      if (filterDimension === 'totalOrders') {
        filterVal = ORDER_TIMES_OPTIONS?.find(item => item.value === filterRule)?.text;
      } else if (filterDimension === 'createTime') {
        filterVal = CREATE_TIME_OPTIONS?.find(item => item.value === filterRule)?.text;
      } else if (filterDimension === 'visitTime') {
        const allSelectFilterByRecentVisit = formatSelectedOption(
          filterRule as FilterByRecentVisit[],
          ARRIVAL_STATUS_OPTIONS
        );
        const filterLabelArr = (allSelectFilterByRecentVisit as FilterByRecentVisit[])?.map(item => {
          const foundItem = ARRIVAL_STATUS_OPTIONS.find(dataItem => dataItem.value === item);
          return foundItem ? foundItem?.text : null;
        });
        filterVal = filterLabelArr?.length > 0 ? filterLabelArr?.join(',') : '全选';
      }
      sendEvent(TrackerCustomEvent.ViewMemberRecordSortClk, {
        et: 'CLK',
        c1: filterLabel,
        c2: filterVal,
      });
    },
    [sendEvent]
  );

  const getFilterOptions = (value: FilterDimension) => {
    const options =
      value === 'totalOrders'
        ? ORDER_TIMES_OPTIONS
        : value === 'createTime'
        ? CREATE_TIME_OPTIONS
        : value === 'visitTime'
        ? ARRIVAL_STATUS_OPTIONS
        : null;
    return options;
  };

  const getSelectParams = useCallback(
    (value: FilterDimension) => {
      const options = getFilterOptions(value);
      setFilterOptions(options);

      let defaultFilterRule: SortOrder | FilterByRecentVisit[] = options?.[0]?.value as SortOrder;
      if (value === 'visitTime') {
        defaultFilterRule = options?.map(item => item.value) as FilterByRecentVisit[];
      }
      setFilterRule(defaultFilterRule);

      handleSendEvent(value, defaultFilterRule);

      getSearchMemberRecord(getParams(value, defaultFilterRule), true);
    },
    [getSearchMemberRecord, handleSendEvent]
  );

  const handleClick = useCallback(
    value => {
      if (filterDimension === value) {
        return;
      }
      setFilterDimension(value);
      getSelectParams(value);
      scrollViewRef.current?.bsInstance.scrollTo(0, 0);
    },
    [filterDimension, getSelectParams]
  );

  const handleChange = useCallback(
    value => {
      setFilterRule(value);
      //埋点事件上报
      handleSendEvent(filterDimension, value);
      getSearchMemberRecord(getParams(filterDimension, value), true);
      scrollViewRef.current?.bsInstance.scrollTo(0, 0);
    },
    [filterDimension, getSearchMemberRecord, handleSendEvent]
  );

  useEffect(() => {
    const filterDimension = state?.orderBy || 'totalOrders';
    const filterRule = state?.orderBySort || 'desc';
    const options = getFilterOptions(filterDimension);
    setFilterOptions(options);
    setFilterDimension(filterDimension);
    setFilterRule(filterRule);
    getSearchMemberRecord(getParams(filterDimension, filterRule), true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={cx('container')}>
      <div className={cx('filter-option')}>
        <div className={cx('service-select')}>
          {SERVICE_SELECT_OPTIONS?.map(item => (
            <div
              className={cx('service-select-item', { active: filterDimension === item.value })}
              key={item.value}
              onClick={() => handleClick(item.value)}
            >
              {item.text}
            </div>
          ))}
        </div>
        <div className={cx('select-box')}>
          <MultipleSelect
            list={filterOptions}
            value={filterRule}
            onChange={handleChange}
            allSelectText="全选"
            multiple={filterDimension === 'visitTime'}
          />
        </div>
      </div>

      <ScrollView autoRefresh ref={scrollViewRef} className={cx('record-list')} onPullingUp={loadMore}>
        {memberRecordList?.length ? (
          <div className={cx('list-wrapper')}>
            <List>
              <div className={cx('member-list')}>
                {memberRecordList?.map(
                  item =>
                    item && (
                      <MemberRecordItem key={item.id} {...item} orderBy={filterDimension} orderBySort={filterRule} />
                    )
                )}
              </div>
            </List>
            <div className={cx('load-data-info')}>{hasMoreData ? '正在加载更多...' : ''}</div>
          </div>
        ) : isLoading ? (
          <div className={cx('loading-wrapper')}>
            <DotLoading />
            正在加载数据
          </div>
        ) : (
          <EmptyBox className={cx('empty-box')} />
        )}
      </ScrollView>
    </div>
  );
};

export default MemberRecord;
